// Branded sign-in (Sleek's own screen). Direct-grant against Keycloak — no hosted UI.
// "Remember me" persists the session across tab closes; a theme toggle lets you flip light/dark here.
import { html, signal, navigate } from '../shared/t4.js';
import { login, rememberedUsername } from '../shared/auth.js';
import { theme, toggleTheme } from '../shared/theme.js';
import { icon } from '../shared/icons.js';

export function loginScreen(){
  document.body.classList.remove('vt');   // login is aboo-skinned (vt.css is tenant-only)
  // The same SPA serves the platform-admin console (admin*.aboo.co.za) and the tenant app
  // (app*.aboo.co.za); brand the sign-in for whichever host it is loaded on, so a business
  // owner isn't greeted by "aboo admin — Platform administration".
  const isAdminHost = /^admin[.-]/.test(location.hostname);
  // Deployed hosts always start with 'admin' or 'app' — anything else (localhost, previews) is
  // ambiguous and needs the developer toggle so both surfaces are reachable in dev.
  const isKnownHost = /^(admin|app)[.-]/.test(location.hostname);
  const username = signal(rememberedUsername());   // prefill remembered username (#4)
  const password = signal('');
  const remember = signal(true);
  const error    = signal('');
  const loading  = signal(false);
  // Surface — on a KNOWN host the hostname decides ('admin*' → admin, 'app*' → companies) and the
  // toggle is hidden. On localhost/preview hosts we show a Companies/Admin toggle so a dev can pick
  // which side to test. Kept as a signal so the branding above and the click handlers react to it.
  const loginSurface = signal(isAdminHost ? 'admin' : 'companies');

  async function submit(e){
    e.preventDefault();
    loading.value = true; error.value = '';
    try {
      await login(username.value, password.value, remember.value, loginSurface.value);
      navigate(loginSurface.value === 'admin' ? '/' : '/home');   // route by chosen surface, not realm
    } catch(err){
      error.value = err.message || 'Sign-in failed';
    }
    loading.value = false;
  }

  return html`
    <div class="login-wrap">
      <button type="button" class="theme-toggle login-toggle" title="Toggle light / dark"
              @click=${() => toggleTheme()}>${() => theme.value === 'dark' ? icon('sun', {size: 18}) : icon('moon', {size: 18})}</button>
      <form class="login-card card" @submit=${submit}>
        <div class="card-body">
          <div class="brand-lockup">
            <span class="brand-dot"></span><span class="brand-name">aboo</span>${() => loginSurface.value === 'admin' ? html`<span class="brand-sub">admin</span>` : ''}
          </div>
          <h1 class="login-title">Sign in</h1>
          <p class="login-hint mb-4">${() => loginSurface.value === 'admin' ? 'Platform administration' : 'Sign in to your account'}</p>

          ${isKnownHost ? '' : html`
          <div class="login-kind" role="tablist" aria-label="Sign in to">
            <button type="button" role="tab" class=${() => 'login-kind-btn' + (loginSurface.value === 'companies' ? ' on' : '')}
                    aria-selected=${() => loginSurface.value === 'companies' ? 'true' : 'false'}
                    @click=${() => { loginSurface.value = 'companies'; error.value = ''; }}>Companies</button>
            <button type="button" role="tab" class=${() => 'login-kind-btn' + (loginSurface.value === 'admin' ? ' on' : '')}
                    aria-selected=${() => loginSurface.value === 'admin' ? 'true' : 'false'}
                    @click=${() => { loginSurface.value = 'admin'; error.value = ''; }}>Admin</button>
          </div>`}

          <label class="form-label">Email or username</label>
          <input class="form-control mb-3" type="text" autocomplete="username" placeholder="you@company.co.za"
                 .value=${username} @input=${e => { username.value = e.target.value; }}>

          <label class="form-label">Password</label>
          <input class="form-control mb-1" type="password" autocomplete="current-password" placeholder="Your password"
                 @input=${e => { password.value = e.target.value; }}>
          <div class="login-forgot mb-3"><a @click=${() => { try { sessionStorage.setItem('sleek_forgot_surface', loginSurface.value); } catch { /* storage blocked */ } navigate('/forgot'); }}>Forgot password?</a></div>

          <label class="form-check login-remember">
            <input type="checkbox" .checked=${remember} @change=${e => { remember.value = e.target.checked; }}> Remember me
          </label>

          ${() => error.value ? html`<div class="alert alert-danger py-2 mb-3">${error}</div>` : null}

          <button class="btn btn-primary btn-block" type="submit"
                  ?disabled=${() => loading.value || !username.value || !password.value}>
            ${() => loading.value ? 'Signing in…' : 'Sign in'}
          </button>
          <p class="login-alt mt-3">New to aboo? <a @click=${() => navigate('/register')}>Create an account</a></p>
        </div>
      </form>
    </div>`;
}
